import { AxiosResponse } from 'axios';
import { FormikProps, FormikValues } from 'formik';
import { Layout } from 'react-grid-layout';
import { IntlShape } from 'react-intl';
import { IValidationApiResponse } from '../../Services/FormikValidatorService';
import { IWidget, IWidgetType } from './Widgets/types';
import {
  CardItem,
  CategoryApiResponse,
  CustomerApiResponse,
  GetTemplateApiResponse,
  GridApiResponse,
  GridError,
  GridSizeValue,
  InitialFormDataApiResponse,
  OnlineTemplateData,
  TypeApiResponse,
  WidgetInRowsAndColumns,
  OnlineTemplateFormValues,
} from './Templates/types';
import { getRequest, postRequest, putRequest } from '../../Services/HttpService';

export const IS_PUBLIC = 'public';
export const MIN_GRID_ROWS = 1;
export const MIN_GRID_COLS = 8;
export const MAX_GRID_ROWS = 3;
export const MAX_GRID_COLS = 12;

/**
 * @returns Promise<IInitialFormDataApiResponse | null>
 */
export const getInitialFormData = async (): Promise<InitialFormDataApiResponse | null> => {
  const response = await getRequest<InitialFormDataApiResponse | false>(
    '/v1/online-map/templates/form/init'
  );
  if (response) {
    return response as InitialFormDataApiResponse;
  }
  return null;
};

/**
 * @returns Promise<ITypeApiResponse[] | null>
 */
export const getTemplateTypes = async (): Promise<TypeApiResponse[] | null> => {
  const response = await getRequest<TypeApiResponse[] | false>(
    '/v1/online-map/templates/form/type'
  );
  if (response) {
    return response as TypeApiResponse[];
  }
  return null;
};

/**
 * @returns Promise<ITypeApiResponse[] | null>
 */
export const getWidgetTypes = async (): Promise<TypeApiResponse[] | null> => {
  const response = await getRequest<TypeApiResponse[] | false>(
    '/v1/online-map/templates/form/widgetType'
  );
  if (response) {
    return response as TypeApiResponse[];
  }
  return null;
};

/**
 * @param  {WidgetType[]} types
 * @param  {IWidget[]} widgets
 * @param  {string} category
 * @returns IWidgetType
 */
export const getWidgetTypesFromWidgetList = (
  types: IWidgetType[],
  widgets: IWidget[],
  category: string
): IWidgetType[] => {
  const typesList = widgets
    .filter((widget: IWidget) => widget.category === category)
    .map((widget: IWidget) => widget.type)
    .filter((value: string, index: number, array: string[]) => array.indexOf(value) === index);
  const widgetTypes = types.filter((widgetType: IWidgetType) =>
    typesList.includes(widgetType.value)
  );
  return widgetTypes;
};

/**
 * @returns Promise<ICustomerApiResponse[] | null>
 */
export const getCustomers = async (): Promise<CustomerApiResponse[] | null> => {
  const response = await getRequest<CustomerApiResponse[] | false>(
    '/v1/online-map/templates/form/customerId'
  );
  if (response) {
    return response as CustomerApiResponse[];
  }
  return null;
};

/**
 * @returns Promise<IGridApiResponse | null>
 */
export const getSizes = async (): Promise<GridApiResponse | null> => {
  const response = await getRequest<GridApiResponse | false>('/v1/online-map/templates/form/grid');
  if (response) {
    return response as GridApiResponse;
  }
  return null;
};

/**
 * @returns Promise<ICategoryApiResponse[] | null>
 */
export const getWidgetCategories = async (): Promise<CategoryApiResponse[] | null> => {
  const response = await getRequest<CategoryApiResponse[] | false>(
    '/v1/online-map/templates/form/widgetCategory'
  );
  if (response) {
    return response as CategoryApiResponse[];
  }
  return null;
};

/**
 * @returns Promise<IValidationApiResponse | null>
 */
export const getValidation = async (): Promise<IValidationApiResponse | null> => {
  const response = await getRequest<IValidationApiResponse | false>(
    '/v1/online-map/templates/validation'
  );
  if (response) {
    return response as IValidationApiResponse;
  }
  return null;
};

/**
 * Get widgets by category
 * @param widgets IWidget[]
 * @param category string
 * @returns IWidget[]
 */
export const getWidgetsByCategory = (
  widgets: IWidget[],
  category: string,
  columnsInRows: number | false = false
): IWidget[] | WidgetInRowsAndColumns => {
  const filteredWidgets = widgets.filter((widget: IWidget) => widget.category === category);
  if (!columnsInRows) {
    return filteredWidgets;
  }
  let row = 0;
  const columnData: WidgetInRowsAndColumns = {};
  for (let i = 0; i < filteredWidgets.length; i += 1) {
    if (!columnData[row]) {
      columnData[row] = [];
    }

    if (i % columnsInRows === 0 && i !== 0) {
      row += 1;
      if (!columnData[row]) {
        columnData[row] = [];
      }
    }
    columnData[row].push(filteredWidgets[i]);
  }
  return columnData;
};

/**
 * Get widgets by type
 * @param widgets IWidget[]
 * @param type string
 * @returns IWidget[]
 */
export const getWidgetsByType = (
  widgets: IWidget[],
  type: string
): IWidget[] | WidgetInRowsAndColumns => {
  const filteredWidgets = widgets.filter((widget: IWidget) => widget.type === type);
  return filteredWidgets;
};

/**
 * Get available widgets by type
 * @param  {IWidget[]} widgets
 * @param  {string} type
 * @param  {CardItem[]} cards
 * @param  {number|false} columnsInRows
 * @returns IWidget[]
 */
export const getAvailableWidgetsByType = (
  widgets: IWidget[] | undefined,
  type: string,
  cards: CardItem[],
  columnsInRows: number | false = false
): IWidget[] | WidgetInRowsAndColumns => {
  if (!widgets) {
    return [];
  }
  const usedWidgetsInGrid: string[] = [];
  cards
    .filter((card: CardItem) => card.widgets.length > 0)
    .map((card: CardItem) => {
      card.widgets.map((widget: Layout) => {
        usedWidgetsInGrid.push(widget.i);
      });
    });

  const filteredWidgets = widgets.filter(
    (widget: IWidget) => widget.type === type && !usedWidgetsInGrid.includes(widget.gridName)
  );
  if (!columnsInRows) {
    return filteredWidgets;
  }
  let row = 0;
  const columnData: WidgetInRowsAndColumns = {};
  for (let i = 0; i < filteredWidgets.length; i += 1) {
    if (!columnData[row]) {
      columnData[row] = [];
    }

    if (i % columnsInRows === 0 && i !== 0) {
      row += 1;
      if (!columnData[row]) {
        columnData[row] = [];
      }
    }
    columnData[row].push(filteredWidgets[i]);
  }
  return columnData;
};

export const showGridAndWidgets = (values: OnlineTemplateFormValues): boolean => {
  if (
    !values.name ||
    !values.active ||
    !values.active ||
    // !formikProps.values.size ||
    !values.type
  ) {
    return false;
  }
  if (values.type === IS_PUBLIC && !values.customerId) {
    return false;
  }
  return true;
};

export const createTemplate = async (
  data: OnlineTemplateData
): Promise<AxiosResponse<any> | null> => {
  const response = await postRequest<OnlineTemplateData, AxiosResponse | false>(
    'v1/online-map/templates/add',
    data
  );

  if (response) {
    return response;
  }
  return null;
};

export const updateTemplate = async (id: number, data: OnlineTemplateData): Promise<any> => {
  const response = await putRequest<OnlineTemplateData, any>(
    `v1/online-map/templates/${id}/edit`,
    data
  );

  if (response) {
    return response;
  }
  return null;
};

/**
 * @param  {IWidget[]} widgets
 * @param  {WidgetType[]} types
 * @param  {string} category
 * @returns IWidgetType
 */
export const getWidgetTypesByCategory = (
  widgets: IWidget[],
  types: IWidgetType[],
  category: string
): IWidgetType[] => {
  if (widgets.length > 0 && category) {
    const widgetsInCategory = widgets.filter((widget: IWidget) => widget.category === category);
    const resArr: IWidget[] = [];
    let widgetTypes: any = [];
    widgetsInCategory.filter((w: IWidget) => {
      const i = resArr.findIndex((ww: IWidget) => w.type === ww.type);
      if (i <= -1) {
        resArr.push(w);
      }
      return null;
    });
    widgetTypes = resArr.map((widget: IWidget) => {
      const type = types.find((widgetType: IWidgetType) => widgetType.value === widget.type);
      return type;
    });
    if (widgetTypes) {
      return widgetTypes;
    }
  }
  return [];
};

/**
 * @param  {FormikProps<FormikValues>} formikProps
 * @param  {string} gridName
 * @returns void
 */
export const removeWidgetFromGrid = (
  formikProps: FormikProps<FormikValues>,
  gridName: string
): void => {
  let cardIndex: number | null = null;
  formikProps.values.cards.map((card: CardItem, index: number) => {
    const widget = card.widgets.find((item: Layout) => item.i === gridName);
    if (widget) {
      cardIndex = index;
    }
  });
  if (cardIndex !== null) {
    const newCards = [...formikProps.values.cards];
    newCards[cardIndex].widgets = formikProps.values.cards[cardIndex].widgets.filter(
      (layout: Layout) => layout.i !== gridName
    );
    const newValues = { ...formikProps.values, cards: newCards };
    formikProps.setValues(newValues);
  }
};

/**
 * @param  {Layout[]} grid
 * @param  {number} maxRows
 * @param  {number} maxColumns
 * @param  {number} index
 * @param  {IntlShape} intl
 * @returns IGridError
 */
export const validateWidgetsInGrid = (
  grid: Layout[],
  maxColumns: number,
  maxRows: number,
  intl: IntlShape
): GridError | null => {
  const error: GridError = {};

  grid.forEach((item: Layout) => {
    if (item.x + item.w > maxColumns) {
      console.info('Widget exceeds max columns.');
      error.columns = intl.formatMessage(
        { id: 'onlineTemplates.validationErrors.widgetExceedsMaxColumns' },
        { columns: maxColumns }
      );
    }
    if (item.y + item.h > maxRows) {
      error.rows = intl.formatMessage(
        { id: 'onlineTemplates.validationErrors.widgetExceedsMaxRows' },
        { rows: maxRows }
      );
      console.info('Widget exceeds max rows.');
    }
  });
  if (Object.keys(error).length === 0) {
    return null;
  }
  return error;
};

/**
 * @param  {Layout[]} grid
 * @param  {number} maxColumns
 * @param  {number} maxRows
 * @returns Layout[]
 */
export const reshuffleWidgetsInGrid = (
  grid: Layout[],
  maxColumns: number,
  maxRows: number
): Layout[] => {
  const widgetsOutOfGrid = grid
    .filter(
      (item: Layout) =>
        item.x > maxColumns - 1 ||
        item.y > maxRows - 1 ||
        // (item.x === maxColumns - 1 && item.w > 1) ||
        item.x + item.w > maxColumns - 1
    )
    .map((item: Layout) => item.i);

  // const widgetsInGrid = grid
  //   .filter((item: Layout) => item.x <= maxColumns - 1 && item.y <= maxRows - 1)
  //   .map((item: Layout) => item.i)
  //   .filter((id: string) => !widgetsOutOfGrid.includes(id));

  const newGrid = grid.map((item: Layout) => {
    if (widgetsOutOfGrid.includes(item.i)) {
      const newItem = {
        ...item,
        y: item.y + 1,
      };
      return newItem;
    }
    return item;
  });

  return newGrid;
};

/**
 * @param  {number | undefined} id
 * @returns Promise<IGetTemplateApiResponse | null>
 */
export const getTemplateData = async (
  id: number | undefined | null
): Promise<GetTemplateApiResponse | null | undefined> => {
  if (!id) {
    return undefined;
  }
  const response = await getRequest<GetTemplateApiResponse | false>(
    `v1/online-map/templates/${id}`
  );

  if (response) {
    return response as GetTemplateApiResponse;
  }
  return null;
};

/**
 * @param  {CardItem[]} cards
 * @returns IGridSizeValue
 */
export const getTemplateSizeByCards = (cards: CardItem[]): GridSizeValue => {
  let rows = 1;
  let cols = 8;
  cards.map((item: CardItem) => {
    item.widgets.map((widget: Layout) => {
      const columnSize = widget.x + widget.w;
      const rowSize = widget.y + widget.h;
      if (columnSize > cols) {
        cols = columnSize;
      }
      if (rowSize > rows) {
        rows = rowSize;
      }
    });
  });
  const gridSize: GridSizeValue = {
    rows,
    columns: cols,
  };
  return gridSize;
};

export const storeWidgetsInLocalStorage = (cards: CardItem[]): void => {
  const widgets = {};
  cards.forEach((card: CardItem, index: number) => {
    widgets[index] = card.widgets;
  });
  localStorage.setItem('widgets', JSON.stringify(widgets));
};

export const loadWidgetsFromLocalStorage = (): { [index: number]: Layout[] } | null => {
  const cards = localStorage.getItem('widgets');
  if (cards) {
    return JSON.parse(cards);
  }
  return null;
};

export const removeWidgetsFromLocalStorage = (): void => {
  localStorage.removeItem('widgets');
};
